import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_LOCALE, isNoteName, type NoteName } from '@cavaquinho/shared';
import { useCurrentUser } from '../../../app/AuthProvider.tsx';
import { useAudio, type AudioState } from '../../../lib/audio.js';
import { noteLabel } from '../../../lib/notes.js';
import type { NoteIdentificationPublicPrompt } from '../../../lib/api/types.js';
import { optionGridStyle } from '../option-grid.ts';
import type { ExerciseRendererProps } from '../registry.ts';
import {
  AudioControl,
  type AudioControlState,
} from '../../../ui/components/AudioControl/AudioControl.tsx';
import { ChoiceTile, type ChoiceTileState } from '../../../ui/components/ChoiceTile/ChoiceTile.tsx';
import { Text } from '../../../ui/primitives/Text.tsx';
import { Feedback, isNoteIdentificationFeedback } from './Feedback.tsx';
import styles from './NoteIdentificationRenderer.module.css';

/**
 * The client half of the Exercise FR-006 describes: hear a note, choose a
 * name, be judged (FR-006, FR-043, FR-044, FR-045).
 *
 * `publicPrompt` and `feedback` arrive as `unknown` — the registry's leaf is
 * where they get a shape (registry.ts). Everything below this line is
 * note-identification's own: the audio orb, the option grid, the number
 * keys. None of it is shared with a future Exercise Type, and none of it
 * reaches into the shell beyond the four callbacks and the phase it is given.
 */

/** A guard, not a cast — a malformed prompt is a server contract violation
    worth failing loudly on rather than rendering silently wrong. */
function isNoteIdentificationPrompt(value: unknown): value is NoteIdentificationPublicPrompt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { audioUrl?: unknown; options?: unknown };
  return (
    typeof candidate.audioUrl === 'string' &&
    Array.isArray(candidate.options) &&
    candidate.options.every(isNoteName)
  );
}

const AUDIO_STATE_MAP: Record<AudioState, AudioControlState> = {
  idle: 'idle',
  loading: 'loading',
  playing: 'playing',
  played: 'replay',
  error: 'error',
};

function audioStatusLabel(state: AudioState): string | undefined {
  switch (state) {
    case 'playing':
      return 'Tocando';
    case 'played':
      return 'Tocar novamente';
    case 'loading':
      return 'Carregando';
    case 'error':
      return 'Não foi possível tocar';
    // The idle orb says what to do with it. Without this the first screen of
    // a session is a silent circle above four inert tiles, and the chip is
    // also what carries the state under `prefers-reduced-motion`, where the
    // waveform is frozen (§ 9, § 12).
    default:
      return 'Toque para ouvir';
  }
}

export function NoteIdentificationRenderer({
  promptId,
  publicPrompt,
  onSubmit,
  phase,
  feedback,
}: ExerciseRendererProps) {
  /*
   * The guard runs before every hook below, but the *throw* it can produce
   * does not — every hook must still execute in the same order on every
   * render, so `audioUrl`/`options` fall back to safe, unused-forever values
   * here and the actual throw waits until after the last hook call.
   */
  const audioUrl = isNoteIdentificationPrompt(publicPrompt) ? publicPrompt.audioUrl : null;
  const options = isNoteIdentificationPrompt(publicPrompt) ? publicPrompt.options : [];

  const locale = useCurrentUser()?.locale ?? DEFAULT_LOCALE;
  const audio = useAudio(audioUrl);
  const [selectedNote, setSelectedNote] = useState<NoteName | null>(null);

  /*
   * A selection locks the moment it is made, before `onSubmit`'s promise ever
   * settles. That is what turns a second click or a duplicate key press
   * during `submitting` into a client-side no-op — the server's 409 on an
   * already-submitted attempt is the second line of defence, not the first
   * (contracts/learner-api.md "already submitted — the original result").
   */
  const submissionLockRef = useRef(false);

  /*
   * Reset on the ATTEMPT, not on the audio URL.
   *
   * Keyed on `audioUrl` this was a dead end the learner could not escape.
   * Two consecutive Exercises drawing the same pitch is an ordinary outcome
   * of the RNG, not a rarity — and when it happens the URL does not change,
   * so an effect keyed on it never re-runs, `submissionLockRef` stays `true`
   * from the previous submission, and `handleSelect` silently refuses every
   * click and every digit key for the whole of the new Exercise. Silently:
   * the tiles look enabled, nothing is announced, and nothing on screen
   * explains it.
   *
   * `promptId` is the identity that actually changes once per Exercise,
   * which is what this reset is about. The shell additionally gives this
   * component `key={attempt.promptId}` (PracticeShell.tsx), so in practice
   * a new attempt arrives at a freshly mounted renderer and this effect is
   * belt to that braces — deliberately, because the guarantee "a new attempt
   * starts unlocked" should not rest on a caller remembering to pass a key.
   */
  useEffect(() => {
    setSelectedNote(null);
    submissionLockRef.current = false;
  }, [promptId]);

  const handleSelect = useCallback(
    (note: NoteName) => {
      if (submissionLockRef.current) return;
      if (!audio.heard || audio.state === 'error') return; // FR-068, defense in depth alongside the disabled tile
      submissionLockRef.current = true;
      setSelectedNote(note);
      onSubmit({ selectedNote: note }).catch(() => {
        // The shell surfaces the failure (data-testid="submit-failed") and
        // offers its own retry; releasing the lock here is what lets the
        // learner pick again instead of being stuck mid-selection.
        submissionLockRef.current = false;
      });
    },
    [audio.heard, audio.state, onSubmit],
  );

  /*
   * FR-043–FR-045 — number keys `1`–`k` follow the *presented* option count,
   * which is note-identification's own affordance and not a platform one.
   *
   * ONE LISTENER, ATTACHED FOR THE WHOLE LIFE OF THE RENDERER, with the gate
   * inside the handler rather than around the subscription. The obvious
   * shape — an effect that returns early while the note has not been heard,
   * so no listener exists yet — drops a keypress made in the window between
   * React painting the enabled tiles and the passive effect that would have
   * attached the listener running. That window is short, but it is real: a
   * learner whose finger is already on the key when the note finishes
   * playing presses into it, and their answer vanishes with no feedback and
   * nothing on screen to explain it.
   *
   * Reading the gate from a ref keeps the listener's own identity stable
   * (nothing to re-subscribe) while the values it reads stay current on
   * every render — so there is no moment when a digit is unheard, and no
   * moment when a stale closure answers with the previous Exercise's
   * options.
   */
  const gateRef = useRef({ phase, heard: audio.heard, state: audio.state, options, handleSelect });
  gateRef.current = { phase, heard: audio.heard, state: audio.state, options, handleSelect };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const {
        phase: currentPhase,
        heard,
        state,
        options: currentOptions,
        handleSelect: select,
      } = gateRef.current;

      // The same gate the tiles enforce through `disabled`, checked again
      // because a `window` listener bypasses a disabled DOM control entirely
      // (FR-068).
      if (currentPhase !== 'answering' || !heard || state === 'error') return;

      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }

      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0 || index >= currentOptions.length) return;

      const note = currentOptions[index];
      if (!note) return; // unreachable given the bounds check above; satisfies noUncheckedIndexedAccess
      select(note);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function tileState(note: NoteName): ChoiceTileState {
    /*
      A RESOLVED TILE IS A RECORD, NOT A CONTROL, so it outranks the audio
      gate below. It has to: an answer the learner gave and came back to — the
      resume path, where the shell opens straight into `feedback`
      (PracticeShell.tsx) — is read on a fresh renderer where the clip has not
      been played this visit, and gating on that would grey out the very tiles
      that say which note was theirs and which was right (FR-069).

      Nothing is loosened by the order. These states carry no `onSelect` the
      learner could reach, `handleSelect` keeps its own FR-068 check, and the
      phase is no longer `answering`.
    */
    if (feedback && isNoteIdentificationFeedback(feedback)) {
      if (note === feedback.correctNote) return 'resolved-correct';
      if (note === feedback.selectedNote) return 'resolved-miss';
      return 'unchosen';
    }

    // FR-068 — no option is choosable for a note the learner was never able
    // to hear, whatever the phase.
    if (!audio.heard || audio.state === 'error') return 'disabled';

    if (phase === 'submitting' && note === selectedNote) return 'selected';
    return 'default';
  }

  if (!isNoteIdentificationPrompt(publicPrompt)) {
    throw new Error(
      'NoteIdentificationRenderer received a publicPrompt that is not { audioUrl, options }',
    );
  }

  /*
   * BOTH OUTCOMES RESOLVE IN PLACE.
   *
   * The tiles are the point either way: § 14's resolved states are what show
   * the learner which option was theirs and which was right, beside a panel
   * that names both (FR-069). A hit that swapped the whole composition for a
   * full-screen statement made the two outcomes read as two different screens,
   * and the learner had to find their bearings again on the one they had just
   * earned. § 4's rise in intensity on success lives inside the panel instead.
   *
   * The shell keeps the advance and its FR-023 timing gate either way; this
   * component never decides when the learner may move on.
   */
  const showHint = !audio.heard && audio.state !== 'error';

  return (
    <div className={styles.root} data-resolved={feedback ? 'true' : undefined}>
      {/*
        The orb and the question are ONE GROUP, and the group takes whatever
        height the screen has left over.

        § 1.5 makes the sound the protagonist, and on a phone that is a claim
        about position as much as about size: the thing that makes the noise
        sits in the middle of the screen, the answer surfaces sit under the
        thumb, and the space between them is what says which of the two the
        learner is meant to attend to first.
      */}
      <div className={styles.prompt}>
        <AudioControl
          state={AUDIO_STATE_MAP[audio.state]}
          label={audio.heard ? 'Ouvir a nota novamente' : 'Ouvir a nota'}
          /*
            The chip says what to do with the orb, and once the answer is
            resolved there is nothing left to do with it but replay — which
            the `replay` state's own ↺ already says, and which the button's
            label says for assistive technology. Nothing is lost under
            `prefers-reduced-motion` either: the chip carries state there
            because the rotation and the waveform are frozen (§ 9), and a
            replay orb has neither running.
          */
          statusLabel={feedback ? undefined : audioStatusLabel(audio.state)}
          errorMessage="Não foi possível tocar a nota. Tente novamente."
          onActivate={audio.state === 'error' ? audio.retry : audio.play}
        />

        {/*
          THE QUESTION — § 5.1 gives `display-lg` to "the primary question", and
          this screen's other display level is spent nowhere else (§ 5.2).
          Beneath the orb rather than above it, because § 1.5 makes the sound the
          protagonist: the learner meets the object that makes the noise first,
          and the words explain what to do with it rather than standing in for
          it.

          It is the page's `h1`. The practice shell holds no heading of its own —
          the Lesson's name is a quiet line beside the task bar — so the document
          outline names the thing actually being asked.

          The copy is note-identification's, not the shell's. Another Exercise
          Type asks a different question, in its own renderer (SC-009).
        */}
        <div className={styles.question}>
          <Text as="h1" variant="display-lg">
            Qual nota você ouviu?
          </Text>

          {/*
            § 12 — a disabled control says WHY, rather than merely dimming. The
            options are gated on the note having been heard (FR-068), and until
            it has been the grid is four muted tiles with nothing on screen to
            explain them. One line, and it goes quiet as soon as it stops
            being true — hidden rather than removed, so its line stays
            reserved and the prompt above it doesn't shift.
          */}
          <div
            className={showHint ? undefined : styles.hintHidden}
            aria-hidden={showHint ? undefined : true}
          >
            <Text as="p" variant="body-sm" tone="muted">
              Ouça a nota para escolher.
            </Text>
          </div>
        </div>
      </div>

      {/* Order is the server's (already shuffled) — re-sorting here would
          make the key hints lie about which digit selects which tile. */}
      <div className={styles.options} style={optionGridStyle(options.length)} data-testid="options">
        {options.map((note, index) => (
          <ChoiceTile
            key={note}
            label={noteLabel(note, locale)}
            keyHint={String(index + 1)}
            state={tileState(note)}
            onSelect={() => handleSelect(note)}
          />
        ))}
      </div>

      {feedback ? (
        <div className={styles.feedback}>
          <Feedback feedback={feedback} locale={locale} />
        </div>
      ) : null}
    </div>
  );
}
